import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import type { NavItem } from "@/shared/components/layout/sidebar-nav.config";
import { apiCall } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";

/** Same key as `usePayrollSetupStatus` so the sidebar shares Setup's cache. */
export const payrollSetupStatusKey = (workspaceId: string | undefined) =>
  ["payrollSetupStatus", workspaceId] as const;

const CONFIG_STALE_MS = 30_000;

/// Only `nextStep` is required for nav order. `.passthrough()` keeps the rest
/// of the setup-status payload so a sidebar fetch cannot shrink the cached
/// object the Setup page already reads.
const payrollNavSetupStatusSchema = z
  .object({
    nextStep: z.enum(["choose_template", "review_structure", "enter_salaries", "run_payroll"]),
  })
  .passthrough();
type PayrollNavSetupStatus = z.infer<typeof payrollNavSetupStatusSchema>;

export const PAYROLL_FOLDER_LABEL = "Payroll";
export const PAYROLL_SETUP_GROUP = "Setup";

export function isNavHeading(item: NavItem): boolean {
  return item.heading === true;
}

type NavGroup = { heading: NavItem | null; items: NavItem[] };

function groupNavItems(children: readonly NavItem[]): NavGroup[] {
  const groups: NavGroup[] = [];
  let current: NavGroup = { heading: null, items: [] };
  for (const item of children) {
    if (isNavHeading(item)) {
      if (current.heading !== null || current.items.length > 0) groups.push(current);
      current = { heading: item, items: [] };
    } else {
      current.items.push(item);
    }
  }
  if (current.heading !== null || current.items.length > 0) groups.push(current);
  return groups;
}

function flattenNavGroups(groups: readonly NavGroup[]): NavItem[] {
  return groups.flatMap((group) =>
    group.heading !== null ? [group.heading, ...group.items] : group.items,
  );
}

/** Hide a heading when permission filtering left it with no destinations. */
export function dropEmptyNavHeadings(children: readonly NavItem[]): NavItem[] {
  return flattenNavGroups(groupNavItems(children).filter((group) => group.items.length > 0));
}

/** Recurring order keeps This month first. Incomplete setup lifts Setup above it. */
export function orderPayrollNavChildren(
  children: readonly NavItem[],
  setupFirst: boolean,
): NavItem[] {
  if (!setupFirst) return [...children];
  const groups = groupNavItems(children);
  const setupIndex = groups.findIndex((group) => group.heading?.label === PAYROLL_SETUP_GROUP);
  if (setupIndex <= 0) return [...children];
  const setupGroup = groups[setupIndex];
  if (!setupGroup) return [...children];
  return flattenNavGroups([setupGroup, ...groups.filter((_, index) => index !== setupIndex)]);
}

export function applyPayrollNavOrder(item: NavItem, setupFirst: boolean): NavItem {
  if (item.label !== PAYROLL_FOLDER_LABEL || item.children === undefined) return item;
  return { ...item, children: orderPayrollNavChildren(item.children, setupFirst) };
}

/** True while setup is incomplete. Loading and errors stay on the recurring order. */
export function isPayrollSetupFirst(nextStep: string | undefined): boolean {
  return nextStep !== undefined && nextStep !== "run_payroll";
}

export function usePayrollNavSetupFirst(
  workspaceId: string | undefined,
  enabled: boolean,
): boolean {
  const status = useQuery({
    queryKey: payrollSetupStatusKey(workspaceId),
    queryFn: () =>
      apiCall<PayrollNavSetupStatus>(
        apiPaths.payroll.setup(workspaceId ?? ""),
        { method: "GET" },
        payrollNavSetupStatusSchema,
      ),
    enabled: enabled && Boolean(workspaceId),
    staleTime: CONFIG_STALE_MS,
  });
  return isPayrollSetupFirst(status.data?.nextStep);
}
